import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { removeEmptyFields } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/remove-empty-fields/remove-empty-fields';
import { getCreateTokenDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/token/create-token.form-default-values';
import { CreateTokenModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/token/create-token.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { TokenCreateFormController } from '@app/feature/token/token-create/token-create-form/token-create-form.controller';
import { CreateTokenContextProvider } from '@app/feature/token/token.context';
import { useCpmsBoCreateTokenMutation } from '@service/cpms-bo-token.service';

interface TokenCreateControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const TokenCreateController = ({
  onCancel,
  onSuccess,
}: TokenCreateControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [createTokenMutation] = useCpmsBoCreateTokenMutation();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<CreateTokenModel> = (errors) => {
    Logger.error(errors);
    toast.error(t('$.token-create.error'));
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<CreateTokenModel> = (
    fieldValues,
  ) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    const createToken = removeEmptyFields(fieldValues);

    createTokenMutation(createToken)
      .unwrap()
      .then(() => {
        toast.success(t('$.token-create.success'));
        onSuccess();
      })
      .catch((error) => {
        Logger.error(error);
        toast.error(t('$.token-create.error', { error: t('global.unknown') }));
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER //////////////////////////////////////////////////////////////////

  const data = getCreateTokenDefaultValues();

  return (
    <CreateTokenContextProvider data={data}>
      <TokenCreateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </CreateTokenContextProvider>
  );
};
